// ABOUTME: SPEC-35 — the frontend half of the session UI-state registry: one entry point
// ABOUTME: for hydration envelopes, one auditor for the domains manifest, one offer builder.
//
// The backend's registry (services/session_ui_state.py) declares what persists; envelopes
// arrive as `ui_update/selection_state` with a `domains` manifest naming everything they
// speak for. `applyHydration` routes each domain to its handler and WARNS LOUDLY on any
// manifest entry it has no handler for (AC-6) — a shipped-but-unhandled domain must be
// one console line, never a silent desync.
//
// Server wins, wholesale (G2): every handler REPLACES its surface's state. localStorage
// caches (the wishlist fast-paint) are paint-only by contract — the envelope always
// overwrites them, and `buildSelectionOffer` is how the client volunteers its paint for
// reconciliation right after connect, so a cold server answers with the authoritative
// (possibly empty) truth instead of letting stale rows survive.

/** Every domain this build knows how to land. Pinned by test — extending the backend
 *  registry without extending this set is exactly the drift AC-6 makes visible. */
export const HANDLED_DOMAINS = ['cart', 'wishlist', 'pinned', 'focused_variant', 'stage', 'waitlist'];

/**
 * Land a selection_state envelope's data into the app through per-domain handlers.
 *
 * @param {object} data - msg.data of a `ui_update/selection_state` envelope.
 * @param {{
 *   cart?: (rows: object[]) => void,
 *   wishlist?: (rows: object[]) => void,
 *   pinned?: (rows: object[]) => void,
 *   focused_variant?: (value: {variant_id: string, sheet: object|null}|null) => void,
 *   stage?: (value: {stage: 'collection'|'piece', collection: string, piece_id: string|null}|null) => void,
 *   waitlist?: (value: {joined: object, pending: string[]}|null) => void,
 * }} handlers
 * @returns {{applied: string[], unhandled: string[]}}
 */
export function applyHydration(data, handlers = {}) {
  const applied = [];
  const unhandled = [];
  if (!data || typeof data !== 'object') return { applied, unhandled };

  if (Array.isArray(data.cart) && handlers.cart) { handlers.cart(data.cart); applied.push('cart'); }
  if (Array.isArray(data.wishlist) && handlers.wishlist) { handlers.wishlist(data.wishlist); applied.push('wishlist'); }
  if (Array.isArray(data.pinned) && handlers.pinned) { handlers.pinned(data.pinned); applied.push('pinned'); }
  if ('focused_variant' in data && handlers.focused_variant) {
    handlers.focused_variant(data.focused_variant ?? null);
    applied.push('focused_variant');
  }
  // SPEC-53 (review R3): which stage the reel screen is in — null is the reel itself.
  if ('stage' in data && handlers.stage) {
    handlers.stage(data.stage ?? null);
    applied.push('stage');
  }
  // SPEC-52: which Vesper variants this guest already joined the waitlist for (D-7/D-8) — flag-gated
  // on the backend, so this key is simply absent for every other tenant and `data.domains` never
  // names it there.
  if ('waitlist' in data && handlers.waitlist) {
    handlers.waitlist(data.waitlist ?? null);
    applied.push('waitlist');
  }

  for (const name of Array.isArray(data.domains) ? data.domains : []) {
    if (!HANDLED_DOMAINS.includes(name)) {
      unhandled.push(name);
      console.warn(
        `[SPEC-35] selection_state names a domain this build has no handler for: "${name}" — `
        + 'its state WILL desync until src/state/sessionUiState.js learns it.',
      );
    }
  }
  return { applied, unhandled };
}

/**
 * The client's post-connect offer (SPEC-31 G2): ids only, from the paint caches, so the
 * server can answer with the authoritative envelope. Null when there is nothing to offer
 * — the server's connect-time hydration already covers the warm cases.
 */
export function buildSelectionOffer({ wishlistItems = [], cartItems = [] } = {}) {
  const ids = (rows) => (Array.isArray(rows) ? rows : [])
    .map((r) => r && r.id)
    .filter(Boolean);
  const wishlist = ids(wishlistItems);
  const cart = ids(cartItems);
  if (wishlist.length === 0 && cart.length === 0) return null;
  const domains = {};
  if (wishlist.length > 0) domains.wishlist = wishlist;
  if (cart.length > 0) domains.cart = cart;
  return { type: 'selection_offer', domains };
}
